import clsx from 'clsx';
import { PropsWithChildren } from 'react';

import { AutomationTestingProps } from '@/types';

export type BadgeType =
  | 'success'
  | 'danger'
  | 'warn'
  | 'info'
  | 'successSecondary'
  | 'dangerSecondary'
  | 'warnSecondary'
  | 'infoSecondary'
  | 'muted'
  | 'indigo';

// the classes are typed in full because tailwind doesn't render the interpolated classes
export const badgeTypeClasses: Record<BadgeType, string> = {
  success: clsx(
    'bg-success-2 text-success-9',
    'th-dark:bg-success-10 th-dark:text-success-3',
    'th-highcontrast:bg-success-10 th-highcontrast:text-success-3'
  ),
  warn: clsx(
    'bg-warning-2 text-warning-9',
    'th-dark:bg-warning-10 th-dark:text-warning-3',
    'th-highcontrast:bg-warning-10 th-highcontrast:text-warning-3'
  ),
  danger: clsx(
    'bg-error-2 text-error-9',
    'th-dark:bg-error-10 th-dark:text-error-3',
    'th-highcontrast:bg-error-10 th-highcontrast:text-error-3'
  ),
  info: clsx(
    'bg-blue-2 text-blue-9',
    'th-dark:bg-blue-10 th-dark:text-blue-3',
    'th-highcontrast:bg-blue-10 th-highcontrast:text-blue-3'
  ),
  // the secondary classes are a bit darker in light mode and a bit lighter in dark mode
  successSecondary: clsx(
    'bg-success-3 text-success-9',
    'th-dark:bg-success-9 th-dark:text-success-3',
    'th-highcontrast:bg-success-9 th-highcontrast:text-success-3'
  ),
  warnSecondary: clsx(
    'bg-warning-3 text-warning-9',
    'th-dark:bg-warning-9 th-dark:text-warning-3',
    'th-highcontrast:bg-warning-9 th-highcontrast:text-warning-3'
  ),
  dangerSecondary: clsx(
    'bg-error-3 text-error-9',
    'th-dark:bg-error-9 th-dark:text-error-3',
    'th-highcontrast:bg-error-9 th-highcontrast:text-error-3'
  ),
  infoSecondary: clsx(
    'bg-blue-3 text-blue-9',
    'th-dark:bg-blue-9 th-dark:text-blue-3',
    'th-highcontrast:bg-blue-9 th-highcontrast:text-blue-3'
  ),
  muted: clsx(
    'bg-gray-3 text-gray-9',
    'th-dark:bg-gray-9 th-dark:text-gray-3',
    'th-highcontrast:bg-gray-9 th-highcontrast:text-gray-3'
  ),
  indigo: clsx(
    'bg-indigo-2 text-indigo-9',
    'th-dark:bg-indigo-10 th-dark:text-indigo-3',
    'th-highcontrast:bg-indigo-10 th-highcontrast:text-indigo-3'
  ),
};

// opt-in border classes per type, matching each type's palette.
// th-highcontrast uses white borders to keep consistency with the high-contrast theme.
const badgeBorderClasses: Record<BadgeType, string> = {
  success: clsx(
    '!border border-solid border-success-4',
    'th-dark:border-success-8',
    'th-highcontrast:border-white'
  ),
  warn: clsx(
    '!border border-solid border-warning-4',
    'th-dark:border-warning-8',
    'th-highcontrast:border-white'
  ),
  danger: clsx(
    '!border border-solid border-error-4',
    'th-dark:border-error-8',
    'th-highcontrast:border-white'
  ),
  info: clsx(
    '!border border-solid border-blue-4',
    'th-dark:border-blue-8',
    'th-highcontrast:border-white'
  ),
  successSecondary: clsx(
    '!border border-solid border-success-4',
    'th-dark:border-success-8',
    'th-highcontrast:border-white'
  ),
  warnSecondary: clsx(
    '!border border-solid border-warning-4',
    'th-dark:border-warning-8',
    'th-highcontrast:border-white'
  ),
  dangerSecondary: clsx(
    '!border border-solid border-error-4',
    'th-dark:border-error-8',
    'th-highcontrast:border-white'
  ),
  infoSecondary: clsx(
    '!border border-solid border-blue-4',
    'th-dark:border-blue-8',
    'th-highcontrast:border-white'
  ),
  muted: clsx(
    '!border border-solid border-gray-4',
    'th-dark:border-gray-8',
    'th-highcontrast:border-white'
  ),
  indigo: clsx(
    '!border border-solid border-indigo-4',
    'th-dark:border-indigo-8',
    'th-highcontrast:border-white'
  ),
};

export interface Props {
  type?: BadgeType;
  className?: string;
  bordered?: boolean;
}

// this component is used in tables and lists in portainer. It looks like this:
// https://www.figma.com/file/g5TUMngrblkXM7NHSyQsD1/New-UI?node-id=76%3A2
export function Badge({
  type = 'info',
  className,
  bordered = false,
  children,
  'data-cy': dataCy,
}: PropsWithChildren<Props> & Partial<AutomationTestingProps>) {
  const baseClasses =
    'inline-flex w-fit items-center !text-xs font-medium rounded-full px-2 py-0.5';

  return (
    <span
      className={clsx(
        baseClasses,
        badgeTypeClasses[type],
        bordered && badgeBorderClasses[type],
        className
      )}
      role="status"
      data-cy={dataCy}
    >
      {children}
    </span>
  );
}
